import { PlusSignCircleIcon, Book02Icon, Book01Icon } from 'hugeicons-react';
import { useState, useRef, useEffect } from 'react';
import '../../../Styles/Layout/Buttons.css';
import '../../../Styles/Layout/Buttons/AddtoSpaceButton.css';
import { Link, useNavigate } from 'react-router-dom';
import { useChatContext } from '../../../contexts/AppDataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { HoverToolTip } from '../Items/HoverToolTip';
import { useUnifiedData } from '../../../contexts/AppDataContext';

interface SpaceButtonProps {
  onActionClick?: () => void;
}

export const SpaceButton: React.FC<SpaceButtonProps> = ({ onActionClick }) => {
  const { currentChatId: chatId, messages, isSpaceChat, currentChatSpace } = useChatContext();
  const { addChatToSpace } = useUnifiedData();
  const auth = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { filteredSpaces } = useUnifiedData();
  
  const DISPLAY_LIMIT = 8;

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !(dropdownRef.current as any).contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleAddToSpace = async (spaceId: string) => {
    try {
      await addChatToSpace(chatId || '', spaceId);
      setIsOpen(false);
    } catch (error) {
      console.error('Error adding chat to space:', error);
    }
  };

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (isSpaceChat || messages?.length > 0) {
      setTimeout(() => {
        setIsOpen(true);
      }, 100);
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 300); // 300ms delay before closing to allow time to move to dropdown
  };

  const handleMouseLeaveQuick = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200); // 200ms delay before closing to allow time to move to dropdown
  };

  const handleSpaceNavigation = () => {
    if (currentChatSpace?.id) {
      navigate(`/spaces/${currentChatSpace.id}`);
    } else {
      navigate('/spaces');
    }
    onActionClick?.();
  };

  return (
    <div 
      className="add-to-space-container" 
      ref={dropdownRef}

    >
      <button
        onClick={() => {
          if (isSpaceChat) {
            handleSpaceNavigation();
          } else if (!isSpaceChat && messages?.length === 0) {
            if (auth.subscription?.plan === 'ultimate') {
              navigate('/spaces');
            } else{
              navigate('/pricing#study-spaces');
            }
          } else {
            // Navigate to spaces when clicked (blend "View All" functionality)
            navigate('/spaces');
          }
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isSpaceChat ? (
          <>
            <Book01Icon size={20} />
            <HoverToolTip
              text={`Open ${currentChatSpace?.title || 'Space'}`}
              position="right"
            />
          </>
        ) : !isSpaceChat && messages?.length === 0 ? (
          <>
            <Book02Icon size={18} />
            <HoverToolTip text="Go to Spaces" position="right" />
          </>
        ) : (
          <>
            <PlusSignCircleIcon size={21} />
          </>
        )}
      </button>
      {isOpen && !isSpaceChat && Array.isArray(filteredSpaces) && (
        <div 
          className={`space-dropdown ${isOpen ? 'open' : 'closing'}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeaveQuick}
        >
          <div className="space-dropdown-header">
            <span>Add to Space</span>
          </div>
          {filteredSpaces.length === 0 ? (
            <div className="no-spaces-message">
              <Link to="/spaces" className="no-spaces-spaces-link">Create A Space</Link>
            </div>
          ) : (
            <>
              {[...filteredSpaces]
                .sort((a, b) => {
                  if (a._creationTime && b._creationTime) {
                    return b._creationTime - a._creationTime;
                  }
                  return 0;
                })
                .slice(0, DISPLAY_LIMIT)
                .map((space) => (
                  <div
                    key={space.id}
                    className="space-option"
                    onClick={() => handleAddToSpace(space.id)}
                  >
                    {space.title}
                  </div>
                ))}
              {filteredSpaces.length > DISPLAY_LIMIT && (
                <Link to="/spaces" className="space-count-indicator">
                  +{filteredSpaces.length - DISPLAY_LIMIT} more spaces
                </Link>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SpaceButton;
